/**
 * @module components/Icons
 * @description Inline SVG icons for the share/print toolbar.
 *
 * External integration: brand marks for Facebook, X and WhatsApp come from
 * the `simple-icons` package (CC0 paths, used here only as share-button
 * glyphs, which is what each brand's guidelines permit). LinkedIn is no
 * longer distributed by simple-icons, so its mark is drawn here as the
 * generic "in" tile.
 */
import type { ReactNode } from "react";
import { siFacebook, siWhatsapp, siX } from "simple-icons";

export type IconName = "print" | "download" | "share" | "email" | "link" | "facebook" | "x" | "linkedin" | "whatsapp";

/** 24 × 24 stroke icons in the house line style. */
const stroke = (children: ReactNode) => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
    </svg>
);

/** 24 × 24 filled brand glyphs. */
const brand = (path: string) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
        <path d={path} />
    </svg>
);

const ICONS: Record<IconName, () => ReactNode> = {
    print: () =>
        stroke(
            <>
                <path d="M6 9V3h12v6" />
                <rect x="3" y="9" width="18" height="8" rx="1.5" />
                <path d="M6 14h12v7H6z" />
            </>
        ),
    download: () =>
        stroke(
            <>
                <path d="M12 3v12" />
                <path d="m7 10 5 5 5-5" />
                <path d="M4 19h16" />
            </>
        ),
    share: () =>
        stroke(
            <>
                <circle cx="18" cy="5" r="2.5" />
                <circle cx="6" cy="12" r="2.5" />
                <circle cx="18" cy="19" r="2.5" />
                <path d="m8.2 10.8 7.6-4.6M8.2 13.2l7.6 4.6" />
            </>
        ),
    email: () =>
        stroke(
            <>
                <rect x="3" y="5" width="18" height="14" rx="1.5" />
                <path d="m3.5 6 8.5 7 8.5-7" />
            </>
        ),
    link: () =>
        stroke(
            <>
                <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" />
                <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />
            </>
        ),
    facebook: () => brand(siFacebook.path),
    x: () => brand(siX.path),
    whatsapp: () => brand(siWhatsapp.path),
    linkedin: () => (
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <rect x="1" y="1" width="22" height="22" rx="3" fill="currentColor" />
            <g fill="var(--c-paper-raised)">
                <rect x="5" y="9.5" width="3" height="9" />
                <circle cx="6.5" cy="6.3" r="1.8" />
                <path d="M10.5 9.5h2.9v1.3c.5-.9 1.6-1.6 3.1-1.6 2.6 0 3.5 1.6 3.5 4.2v5.1h-3v-4.5c0-1.2-.3-2.1-1.5-2.1s-1.9.9-1.9 2.1v4.5h-3.1z" />
            </g>
        </svg>
    ),
};

export function Icon({ name }: { name: IconName }) {
    return <>{ICONS[name]()}</>;
}
